import React, { useState } from 'react';
import Modal from '../../../components/Modal';
import {
    PersonStanding, AlertTriangle, Pill, Shield, Wrench, FileText,
    Plus, X, ChevronRight, ChevronLeft, CheckCircle2, ArrowLeft
} from 'lucide-react';
import frontBodyAsset from '../../../assets/body_map/body-map-front.svg';
import backBodyAsset from '../../../assets/body_map/body-map-back.svg';
import { Select, Button } from '../../../components';

// ─── Constants ───────────────────────────────────────────────────────────────

const INCIDENT_TYPES = [
    { id: 'Fall – Witnessed',    label: 'Fall – Witnessed',    sub: 'Staff saw the fall',     Icon: PersonStanding },
    { id: 'Fall – Unwitnessed',  label: 'Fall – Unwitnessed',  sub: 'Found after the event',  Icon: PersonStanding },
    { id: 'Aggressive Behaviour',label: 'Aggressive Behaviour',sub: 'Verbal or physical',      Icon: AlertTriangle },
    { id: 'Injury',              label: 'Injury',              sub: 'Skin / wound / harm',    Icon: Pill },
    { id: 'Medication Error',    label: 'Medication Error',    sub: 'Missed / wrong dose',    Icon: Pill },
    { id: 'Near Miss',           label: 'Near Miss',           sub: 'No harm caused',         Icon: Shield },
    { id: 'Property Damage',     label: 'Property Damage',     sub: 'Equipment / belongings', Icon: Wrench },
    { id: 'Other',               label: 'Other',               sub: 'Anything else',          Icon: FileText },
];

const MARK_TYPES = [
    'Bruise', 'Cut / Laceration', 'Graze / Abrasion', 'Swelling',
    'Redness', 'Skin tear', 'Burn / Scald', 'Bump / Lump', 'Pressure mark', 'Other',
];

const EMOTIONS = [
    { id: 'Distressed', label: 'Distressed', sub: 'Tearful · agitated',  emoji: '😣', color: '#EF4444' },
    { id: 'Anxious',    label: 'Anxious',    sub: 'Worried · restless',  emoji: '😟', color: '#F97316' },
    { id: 'Unsettled',  label: 'Unsettled',  sub: 'Quiet · withdrawn',   emoji: '😐', color: '#EAB308' },
    { id: 'Settled',    label: 'Settled',    sub: 'Comfortable',         emoji: '🙂', color: '#22C55E' },
    { id: 'Calm',       label: 'Calm',       sub: 'Relaxed · content',   emoji: '😊', color: '#16A34A' },
];

const SEVERITY_COLOR = { Mild: '#22C55E', Moderate: '#F97316', Severe: '#EF4444' };
const STEPS = ['Details', 'Injury & medical', 'Body map', 'Wellbeing', 'Review'];

// ─── Body map zone data ───────────────────────────────────────────────────────
// cx/cy = centroid used for pin placement

const FRONT_ZONES = [
    { id: 'f-head',      label: 'Head',                 cx: 120, cy: 48,  d: 'M120,8 C99,8 86,26 86,48 C86,70 99,88 120,88 C141,88 154,70 154,48 C154,26 141,8 120,8 Z' },
    { id: 'f-neck',      label: 'Neck',                 cx: 120, cy: 93,  d: 'M106,82 L134,82 Q137,82 137,86 L137,104 Q137,107 134,107 L106,107 Q103,107 103,104 L103,86 Q103,82 106,82 Z' },
    { id: 'f-chest',     label: 'Chest',                cx: 120, cy: 165, d: 'M62,128 C62,112 78,107 96,107 L144,107 C162,107 178,112 178,128 L177,222 L63,222 Z' },
    { id: 'f-abdomen',   label: 'Abdomen',              cx: 120, cy: 278, d: 'M63,222 L177,222 C176,250 174,280 172,300 C170,320 162,336 150,342 L90,342 C78,336 70,320 68,300 C66,280 64,250 63,222 Z' },
    { id: 'f-groin',     label: 'Groin / private area', cx: 120, cy: 358, d: 'M104,340 L136,340 L132,360 C130,372 125,378 120,378 C115,378 110,372 108,360 Z' },
    { id: 'f-r-uarm',    label: 'Right upper arm',      cx: 47,  cy: 175, d: 'M62,128 C53,116 41,117 37,131 L33,221 L62,221 Z' },
    { id: 'f-l-uarm',    label: 'Left upper arm',       cx: 193, cy: 175, d: 'M178,128 C187,116 199,117 203,131 L207,221 L178,221 Z' },
    { id: 'f-r-farm',    label: 'Right forearm',        cx: 47,  cy: 270, d: 'M33,221 L62,221 L61,298 C61,314 55,327 47,334 L42,332 C35,325 31,313 31,298 L33,221 Z' },
    { id: 'f-l-farm',    label: 'Left forearm',         cx: 193, cy: 270, d: 'M207,221 L178,221 L179,298 C179,314 185,327 193,334 L198,332 C205,325 209,313 209,298 L207,221 Z' },
    { id: 'f-r-hand',    label: 'Right hand',           cx: 47,  cy: 363, d: 'M41,330 L60,332 C66,330 72,335 72,342 C72,349 66,353 60,352 L62,385 C61,395 56,400 49,400 L44,400 C38,400 34,395 33,386 L31,360 C30,350 34,338 41,330 Z' },
    { id: 'f-l-hand',    label: 'Left hand',            cx: 193, cy: 363, d: 'M199,330 L180,332 C174,330 168,335 168,342 C168,349 174,353 180,352 L178,385 C179,395 184,400 191,400 L196,400 C202,400 206,395 207,386 L209,360 C210,350 206,338 199,330 Z' },
    { id: 'f-r-thigh',   label: 'Right thigh',          cx: 92,  cy: 390, d: 'M68,342 L116,342 L114,438 L73,438 L68,342 Z' },
    { id: 'f-l-thigh',   label: 'Left thigh',           cx: 148, cy: 390, d: 'M172,342 L124,342 L126,438 L167,438 L172,342 Z' },
    { id: 'f-r-knee',    label: 'Right knee',           cx: 93,  cy: 457, d: 'M73,438 L114,438 L112,476 L76,476 Z' },
    { id: 'f-l-knee',    label: 'Left knee',            cx: 147, cy: 457, d: 'M167,438 L126,438 L128,476 L164,476 Z' },
    { id: 'f-r-lleg',    label: 'Right lower leg',      cx: 93,  cy: 527, d: 'M76,476 L112,476 L107,560 C107,572 101,578 93,578 L84,578 C77,578 74,572 74,560 L76,476 Z' },
    { id: 'f-l-lleg',    label: 'Left lower leg',       cx: 147, cy: 527, d: 'M164,476 L128,476 L133,560 C133,572 139,578 147,578 L156,578 C163,578 166,572 166,560 L164,476 Z' },
    { id: 'f-r-foot',    label: 'Right foot',           cx: 87,  cy: 599, d: 'M76,576 L106,576 L108,602 C108,615 98,622 86,622 L78,622 C69,620 67,610 70,600 Z' },
    { id: 'f-l-foot',    label: 'Left foot',            cx: 153, cy: 599, d: 'M164,576 L134,576 L132,602 C132,615 142,622 154,622 L162,622 C171,620 173,610 170,600 Z' },
];

const BACK_ZONES = [
    { id: 'b-head',      label: 'Head',                  cx: 120, cy: 48,  d: 'M120,8 C99,8 86,26 86,48 C86,70 99,88 120,88 C141,88 154,70 154,48 C154,26 141,8 120,8 Z' },
    { id: 'b-neck',      label: 'Neck',                  cx: 120, cy: 93,  d: 'M106,82 L134,82 Q137,82 137,86 L137,104 Q137,107 134,107 L106,107 Q103,107 103,104 L103,86 Q103,82 106,82 Z' },
    { id: 'b-l-shldr',   label: 'Left shoulder',         cx: 90,  cy: 129, d: 'M62,128 C62,112 78,107 96,107 L118,107 L118,150 L63,150 Z' },
    { id: 'b-r-shldr',   label: 'Right shoulder',        cx: 150, cy: 129, d: 'M178,128 C178,112 162,107 144,107 L122,107 L122,150 L177,150 Z' },
    { id: 'b-uback',     label: 'Upper back',            cx: 120, cy: 191, d: 'M63,150 L177,150 L177,232 L63,232 Z' },
    { id: 'b-lback',     label: 'Lower back',            cx: 120, cy: 268, d: 'M63,232 L177,232 C176,260 174,286 172,304 L68,304 C66,286 64,260 63,232 Z' },
    { id: 'b-buttocks',  label: 'Buttocks / sacral area',cx: 120, cy: 330, d: 'M68,304 L172,304 C172,330 164,350 146,356 C133,360 122,350 120,344 C118,350 107,360 94,356 C76,350 68,330 68,304 Z' },
    { id: 'b-l-uarm',    label: 'Left upper arm',        cx: 47,  cy: 175, d: 'M62,128 C53,116 41,117 37,131 L33,221 L62,221 Z' },
    { id: 'b-r-uarm',    label: 'Right upper arm',       cx: 193, cy: 175, d: 'M178,128 C187,116 199,117 203,131 L207,221 L178,221 Z' },
    { id: 'b-l-farm',    label: 'Left forearm',          cx: 47,  cy: 270, d: 'M33,221 L62,221 L61,298 C61,314 55,327 47,334 L42,332 C35,325 31,313 31,298 L33,221 Z' },
    { id: 'b-r-farm',    label: 'Right forearm',         cx: 193, cy: 270, d: 'M207,221 L178,221 L179,298 C179,314 185,327 193,334 L198,332 C205,325 209,313 209,298 L207,221 Z' },
    { id: 'b-l-hand',    label: 'Left hand',             cx: 47,  cy: 363, d: 'M41,330 L60,332 C66,330 72,335 72,342 C72,349 66,353 60,352 L62,385 C61,395 56,400 49,400 L44,400 C38,400 34,395 33,386 L31,360 C30,350 34,338 41,330 Z' },
    { id: 'b-r-hand',    label: 'Right hand',            cx: 193, cy: 363, d: 'M199,330 L180,332 C174,330 168,335 168,342 C168,349 174,353 180,352 L178,385 C179,395 184,400 191,400 L196,400 C202,400 206,395 207,386 L209,360 C210,350 206,338 199,330 Z' },
    { id: 'b-l-thigh',   label: 'Left thigh',            cx: 92,  cy: 409, d: 'M68,342 L116,342 L112,476 L76,476 Z' },
    { id: 'b-r-thigh',   label: 'Right thigh',           cx: 148, cy: 409, d: 'M172,342 L124,342 L128,476 L164,476 Z' },
    { id: 'b-l-calf',    label: 'Left calf',             cx: 93,  cy: 518, d: 'M76,476 L112,476 L107,560 C107,572 101,578 93,578 L84,578 C77,578 74,572 74,560 L76,476 Z' },
    { id: 'b-r-calf',    label: 'Right calf',            cx: 147, cy: 518, d: 'M164,476 L128,476 L133,560 C133,572 139,578 147,578 L156,578 C163,578 166,572 166,560 L164,476 Z' },
    { id: 'b-l-heel',    label: 'Left heel',             cx: 91,  cy: 587, d: 'M78,576 L104,576 Q106,576 106,580 L106,598 L76,598 L76,580 Q76,576 78,576 Z' },
    { id: 'b-r-heel',    label: 'Right heel',            cx: 149, cy: 587, d: 'M162,576 L136,576 Q134,576 134,580 L134,598 L164,598 L164,580 Q164,576 162,576 Z' },
    { id: 'b-l-foot',    label: 'Left foot',             cx: 87,  cy: 612, d: 'M76,598 L106,598 L108,610 C108,620 98,626 86,626 L78,626 C69,624 67,616 70,608 Z' },
    { id: 'b-r-foot',    label: 'Right foot',            cx: 153, cy: 612, d: 'M164,598 L134,598 L132,610 C132,620 142,626 154,626 L162,626 C171,624 173,616 170,608 Z' },
];

// ─── Helper ───────────────────────────────────────────────────────────────────

function computeSeverity(form) {
    const redFlags = form.medication_administered || form.signs_of_pain || form.hit_head || form.signs_of_bleeding;
    const severeMarks = (form.body_map_markers || []).some(m => m.severity === 'Severe');
    const moderateMarks = (form.body_map_markers || []).some(m => m.severity === 'Moderate');
    if (redFlags || severeMarks) return 'Severe';
    if (moderateMarks || form.pain_score >= 5) return 'Moderate';
    return 'Mild';
}

// ─── Step indicator ───────────────────────────────────────────────────────────

function StepIndicator({ current }) {
    return (
        <div className="flex items-center gap-0 mb-6">
            {STEPS.map((s, i) => (
                <React.Fragment key={s}>
                    <div className="flex items-center gap-1.5">
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold shrink-0 ${i <= current ? 'bg-[#2563EB] text-white' : 'bg-gray-200 dark:bg-[#31373f] text-gray-500 dark:text-gray-400'}`}>
                            {i < current ? <CheckCircle2 className="w-4 h-4" /> : i + 1}
                        </div>
                        <span className={`text-[12px] font-medium hidden sm:block ${i === current ? 'text-[#2563EB]' : i < current ? 'text-gray-600 dark:text-gray-400' : 'text-gray-400 dark:text-gray-500'}`}>{s}</span>
                    </div>
                    {i < STEPS.length - 1 && <div className={`flex-1 h-[2px] mx-2 ${i < current ? 'bg-[#2563EB]' : 'bg-gray-200 dark:bg-gray-700'}`} />}
                </React.Fragment>
            ))}
        </div>
    );
}

// ─── Step 1: Details ──────────────────────────────────────────────────────────

function StepDetails({ form, setForm }) {
    return (
        <div>
            <h3 className="text-[17px] font-bold text-gray-800 dark:text-gray-200 mb-1">What happened?</h3>
            <p className="text-[13px] text-gray-500 dark:text-gray-400 mb-5">Select the type of incident, then capture when and where it occurred.</p>

            <label className="text-[13px] font-semibold text-gray-700 dark:text-gray-200 mb-2 block">Type of incident <span className="text-red-500">*</span></label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-5">
                {INCIDENT_TYPES.map(({ id, label, sub, Icon }) => (
                    <button key={id} type="button" onClick={() => setForm(f => ({ ...f, incident_type: id }))}
                        className={`flex items-start gap-2.5 p-3 rounded-xl border text-left transition-all ${form.incident_type === id ? 'border-[#2563EB] bg-blue-50' : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 bg-white dark:bg-[#31373f]'}`}>
                        <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${form.incident_type === id ? 'text-[#2563EB]' : 'text-gray-400 dark:text-gray-500'}`} />
                        <div>
                            <div className={`text-[12px] font-semibold ${form.incident_type === id ? 'text-[#2563EB]' : 'text-gray-700 dark:text-gray-200'}`}>{label}</div>
                            <div className="text-[11px] text-gray-400 dark:text-gray-500">{sub}</div>
                        </div>
                    </button>
                ))}
            </div>

            <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                    <label className="text-[13px] font-semibold text-gray-700 dark:text-gray-200 mb-1.5 block">Date &amp; time of incident <span className="text-red-500">*</span></label>
                    <input type="datetime-local" value={form.incident_date}
                        onChange={e => setForm(f => ({ ...f, incident_date: e.target.value }))}
                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-blue-400 dark:bg-[#31373f] dark:text-gray-200 dark:border-gray-700" />
                </div>
                <div>
                    <label className="text-[13px] font-semibold text-gray-700 dark:text-gray-200 mb-1.5 block">Location</label>
                    <input type="text" placeholder="e.g. Bedroom, en-suite, lounge..." value={form.location}
                        onChange={e => setForm(f => ({ ...f, location: e.target.value }))}
                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-blue-400 dark:bg-[#31373f] dark:text-gray-200 dark:border-gray-700" />
                </div>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                    <label className="text-[13px] font-semibold text-gray-700 dark:text-gray-200 mb-1.5 block">Was it witnessed?</label>
                    <div className="flex gap-2">
                        <button type="button" onClick={() => setForm(f => ({ ...f, witnessed: true }))}
                            className={`flex-1 py-2 rounded-lg text-[13px] font-medium border transition ${form.witnessed ? 'bg-green-600 text-white border-green-600' : 'bg-white dark:bg-[#31373f] text-gray-600 dark:text-gray-400 border-gray-200 dark:border-gray-700 hover:border-gray-300'}`}>
                            Witnessed
                        </button>
                        <button type="button" onClick={() => setForm(f => ({ ...f, witnessed: false }))}
                            className={`flex-1 py-2 rounded-lg text-[13px] font-medium border transition ${!form.witnessed ? 'bg-gray-100 dark:bg-[#31373f] text-gray-700 dark:text-gray-200 border-gray-300 dark:border-gray-700' : 'bg-white dark:bg-[#31373f] text-gray-600 dark:text-gray-400 border-gray-200 dark:border-gray-700 hover:border-gray-300'}`}>
                            Unwitnessed
                        </button>
                    </div>
                </div>
                <div>
                    <label className="text-[13px] font-semibold text-gray-700 dark:text-gray-200 mb-1.5 block">Reported by</label>
                    <input type="text" placeholder="Staff name..." value={form.reported_by}
                        onChange={e => setForm(f => ({ ...f, reported_by: e.target.value }))}
                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-blue-400 dark:bg-[#31373f] dark:text-gray-200 dark:border-gray-700" />
                </div>
            </div>

            <div>
                <label className="text-[13px] font-semibold text-gray-700 dark:text-gray-200 mb-1.5 block">Description of incident <span className="text-red-500">*</span></label>
                <textarea rows={3} placeholder="Describe what happened in plain, factual terms — what was seen, position found, surroundings..."
                    value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-[13px] resize-none focus:outline-none focus:ring-2 focus:ring-blue-400 dark:bg-[#31373f] dark:text-gray-200 dark:border-gray-700" />
            </div>
        </div>
    );
}

// ─── Step 2: Injury & Medical ─────────────────────────────────────────────────

function YesNoRow({ label, value, onChange }) {
    return (
        <div className="flex items-center justify-between py-3 px-4">
            <span className="text-[13px] text-gray-700 dark:text-gray-200">{label}</span>
            <div className="flex gap-2">
                <button type="button" onClick={() => onChange(true)}
                    className={`w-16 py-1.5 rounded-lg text-[12px] font-semibold border transition ${value ? 'bg-blue-50 border-[#2563EB] text-[#2563EB]' : 'border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 hover:border-gray-300'}`}>Yes</button>
                <button type="button" onClick={() => onChange(false)}
                    className={`w-16 py-1.5 rounded-lg text-[12px] font-semibold border transition ${!value ? 'bg-blue-50 border-[#2563EB] text-[#2563EB]' : 'border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 hover:border-gray-300'}`}>No</button>
            </div>
        </div>
    );
}

function StepInjury({ form, setForm }) {
    const [personInput, setPersonInput] = useState('');
    const people = form.victims_involved ? form.victims_involved.split(',').map(s => s.trim()).filter(Boolean) : [];

    const addPerson = () => {
        if (!personInput.trim()) return;
        setForm(f => ({ ...f, victims_involved: [...people, personInput.trim()].join(', ') }));
        setPersonInput('');
    };
    const removePerson = (name) => setForm(f => ({ ...f, victims_involved: people.filter(n => n !== name).join(', ') }));

    return (
        <div>
            <h3 className="text-[17px] font-bold text-gray-800 dark:text-gray-200 mb-1">Injury &amp; medical checks</h3>
            <p className="text-[13px] text-gray-500 dark:text-gray-400 mb-5">Quick clinical screen. Anything flagged <strong>Yes</strong> raises the incident severity automatically.</p>

            <div className="border border-gray-100 dark:border-gray-800 rounded-xl divide-y divide-gray-100 dark:divide-gray-800 mb-5">
                <YesNoRow label="Any medication administered?" value={form.medication_administered} onChange={v => setForm(f => ({ ...f, medication_administered: v }))} />
                <YesNoRow label="Any signs of pain?" value={form.signs_of_pain} onChange={v => setForm(f => ({ ...f, signs_of_pain: v }))} />
                <YesNoRow label="Did the resident hit their head?" value={form.hit_head} onChange={v => setForm(f => ({ ...f, hit_head: v }))} />
                <YesNoRow label="Any signs of bleeding?" value={form.signs_of_bleeding} onChange={v => setForm(f => ({ ...f, signs_of_bleeding: v }))} />
            </div>

            <div className="mb-4">
                <label className="text-[13px] font-semibold text-gray-700 dark:text-gray-200 mb-1.5 block">Medical attention <span className="text-[12px] font-normal text-gray-400 dark:text-gray-500">if any</span></label>
                <input type="text" placeholder="e.g. First aid given · GP called · 111 · 999 · none required"
                    value={form.medical_attention} onChange={e => setForm(f => ({ ...f, medical_attention: e.target.value }))}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-blue-400 dark:bg-[#31373f] dark:text-gray-200 dark:border-gray-700" />
            </div>

            <div>
                <label className="text-[13px] font-semibold text-gray-700 dark:text-gray-200 mb-1.5 block">Others involved in the incident</label>
                <div className="flex gap-2 mb-2">
                    <input type="text" placeholder="Add a name (resident, staff, visitor)..." value={personInput}
                        onChange={e => setPersonInput(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addPerson())}
                        className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-blue-400 dark:bg-[#31373f] dark:text-gray-200 dark:border-gray-700" />
                    <button type="button" onClick={addPerson}
                        className="flex items-center gap-1 px-4 py-2 bg-blue-50 border border-[#2563EB] text-[#2563EB] rounded-lg text-[13px] font-semibold hover:bg-blue-100 transition">
                        <Plus className="w-3.5 h-3.5" /> Add
                    </button>
                </div>
                {people.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                        {people.map(p => (
                            <span key={p} className="flex items-center gap-1.5 bg-gray-100 dark:bg-[#31373f] text-gray-700 dark:text-gray-200 px-3 py-1 rounded-full text-[12px]">
                                {p}
                                <button type="button" onClick={() => removePerson(p)} className="text-gray-400 dark:text-gray-500 hover:text-red-500"><X className="w-3 h-3" /></button>
                            </span>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

// ─── Step 3: Body map ─────────────────────────────────────────────────────────

function BodyFigureSVG({ side, markers, activeZoneId, pendingPin, onZoneClick, onPinClick }) {
    const zones   = side === 'front' ? FRONT_ZONES : BACK_ZONES;
    const baseImg = side === 'front' ? frontBodyAsset : backBodyAsset;

    // Convert mouse event to SVG coordinate space
    const getSvgPoint = (e) => {
        const svg = e.currentTarget.ownerSVGElement;
        const pt  = svg.createSVGPoint();
        pt.x = e.clientX;
        pt.y = e.clientY;
        return pt.matrixTransform(svg.getScreenCTM().inverse());
    };

    // Saved marks that belong to this figure side
    const sideMarkers = markers.map((m, globalIdx) => ({ ...m, globalIdx }))
        .filter(m => m.side === side);

    return (
        <svg viewBox="0 0 240 660" xmlns="http://www.w3.org/2000/svg" className="w-full" style={{ display: 'block' }}>
            <style>{`
                .bm-zone { cursor: crosshair; transition: filter 0.12s; }
                .bm-zone:hover { filter: brightness(0.95); }
            `}</style>

            {/* Base body figure — the real SVG asset (silhouette, outlines, L/R) */}
            <image href={baseImg} x="0" y="0" width="240" height="660" preserveAspectRatio="xMidYMid meet" />

            {zones.map(zone => {
                const isActive  = zone.id === activeZoneId;
                const hasMarks  = markers.some(m => m.zoneId === zone.id);
                const topMark   = markers.find(m => m.zoneId === zone.id);
                // Transparent by default so the asset shows through; only tint a
                // zone when it's active or holds a mark.
                const fill      = isActive
                    ? '#BFDBFE'
                    : topMark
                        ? SEVERITY_COLOR[topMark.severity] + '38'
                        : 'transparent';
                const stroke    = isActive
                    ? '#3B82F6'
                    : topMark
                        ? SEVERITY_COLOR[topMark.severity]
                        : 'transparent';
                return (
                    <path key={zone.id} className="bm-zone" d={zone.d}
                        fill={fill} stroke={stroke} strokeWidth={isActive || hasMarks ? 1.5 : 0}
                        onClick={e => onZoneClick(zone, getSvgPoint(e))}>
                        <title>{zone.label}</title>
                    </path>
                );
            })}

            {/* Saved mark pins */}
            {sideMarkers.map(mark => {
                const col = SEVERITY_COLOR[mark.severity];
                return (
                    <g key={mark.id} transform={`translate(${mark.pinX},${mark.pinY})`} style={{ cursor: 'pointer' }}
                        onClick={e => { e.stopPropagation(); onPinClick(mark.globalIdx); }}>
                        <circle r="12" fill={col} opacity="0.22" />
                        <circle r="8.5" fill={col} stroke="#fff" strokeWidth="2.5" />
                        <text y="3.3" textAnchor="middle" fontSize="10" fontWeight="700" fill="#fff"
                            style={{ pointerEvents: 'none', userSelect: 'none' }}>
                            {mark.globalIdx + 1}
                        </text>
                    </g>
                );
            })}

            {/* Pending pin — shown at exact click position while mark panel is open */}
            {pendingPin && pendingPin.side === side && (
                <g transform={`translate(${pendingPin.x},${pendingPin.y})`} style={{ pointerEvents: 'none' }}>
                    <circle r="12" fill="#1e293b" opacity="0.15" />
                    <circle r="7"  fill="#1e293b" stroke="#fff" strokeWidth="2.5" />
                    <circle r="2.5" fill="#fff" />
                </g>
            )}
        </svg>
    );
}

function StepBodyMap({ form, setForm }) {
    const [activeZone,  setActiveZone]  = useState(null);
    const [pendingPin,  setPendingPin]  = useState(null);
    const [editingIdx,  setEditingIdx]  = useState(null);
    const [markForm,    setMarkForm]    = useState({ type: '', severity: 'Mild', notes: '' });

    const markers = form.body_map_markers || [];

    const openNew = (zone, svgPoint) => {
        const side = zone.id.startsWith('f-') ? 'front' : 'back';
        setActiveZone(zone);
        setPendingPin({ x: svgPoint.x, y: svgPoint.y, side });
        setEditingIdx(null);
        setMarkForm({ type: '', severity: 'Mild', notes: '' });
    };

    const openEdit = (globalIdx) => {
        const mark = markers[globalIdx];
        const allZones = [...FRONT_ZONES, ...BACK_ZONES];
        const zone = allZones.find(z => z.id === mark.zoneId);
        setActiveZone(zone);
        setPendingPin({ x: mark.pinX, y: mark.pinY, side: mark.side });
        setEditingIdx(globalIdx);
        setMarkForm({ type: mark.type, severity: mark.severity, notes: mark.notes || '' });
    };

    const cancelMark = () => { setActiveZone(null); setPendingPin(null); };

    const saveMark = () => {
        if (!markForm.type || !activeZone || !pendingPin) return;
        const updated = [...markers];
        if (editingIdx !== null) {
            updated[editingIdx] = {
                ...updated[editingIdx],
                type: markForm.type, severity: markForm.severity, notes: markForm.notes,
            };
        } else {
            updated.push({
                id: Date.now(),
                zoneId: activeZone.id,
                zoneLabel: activeZone.label,
                side: pendingPin.side,
                pinX: pendingPin.x,
                pinY: pendingPin.y,
                type: markForm.type,
                severity: markForm.severity,
                notes: markForm.notes,
            });
        }
        setForm(f => ({ ...f, body_map_markers: updated }));
        setActiveZone(null);
        setPendingPin(null);
    };

    const deleteMark = (idx) => {
        setForm(f => ({ ...f, body_map_markers: markers.filter((_, i) => i !== idx) }));
        setActiveZone(null);
        setPendingPin(null);
    };

    const activeZoneId = activeZone?.id || null;

    return (
        <div>
            {/* Header row */}
            <div className="flex items-center justify-between mb-1">
                <h3 className="text-[17px] font-bold text-gray-800 dark:text-gray-200">Body map</h3>
            </div>

            {/* Legend + hint */}
            <div className="flex items-center justify-between mb-3">
                <div className="flex gap-3 text-[12px]">
                    {Object.entries(SEVERITY_COLOR).map(([s, c]) => (
                        <span key={s} className="flex items-center gap-1.5">
                            <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: c }} />{s}
                        </span>
                    ))}
                </div>
                <span className="text-[12px] text-gray-400 dark:text-gray-500 italic">Tap a region to record an injury mark</span>
            </div>

            <div className="flex gap-4">
                {/* ── Body figures ── */}
                <div className="shrink-0 border border-gray-200 dark:border-gray-800 rounded-xl bg-[#F8FAFC] dark:bg-[#20242c] p-3" style={{ width: 340 }}>
                    <div className="grid grid-cols-2 gap-2">
                        <div className="flex flex-col items-center">
                            <span className="text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-1">Front</span>
                            <BodyFigureSVG side="front" markers={markers}
                                activeZoneId={activeZoneId} pendingPin={pendingPin}
                                onZoneClick={openNew} onPinClick={openEdit} />
                        </div>
                        <div className="flex flex-col items-center">
                            <span className="text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-1">Back</span>
                            <BodyFigureSVG side="back" markers={markers}
                                activeZoneId={activeZoneId} pendingPin={pendingPin}
                                onZoneClick={openNew} onPinClick={openEdit} />
                        </div>
                    </div>
                    {/* Bottom note */}
                    <p className="text-center text-[10px] text-gray-400 dark:text-gray-500 mt-2">
                        L / R shown from the resident's perspective · marks are optional
                    </p>
                </div>

                {/* ── Right panel ── */}
                <div className="flex-1 min-w-0">
                    {activeZone ? (
                        /* Mark form */
                        <div className="flex flex-col gap-3">
                            {/* Panel header */}
                            <div className="flex items-center gap-2">
                                <button type="button" onClick={cancelMark}
                                    className="w-7 h-7 flex items-center justify-center rounded-full border border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-[#31373f] transition shrink-0">
                                    <ArrowLeft className="w-3.5 h-3.5" />
                                </button>
                                <span className="text-[15px] font-bold text-gray-800 dark:text-gray-200">
                                    {editingIdx !== null ? 'Edit mark' : 'New mark'}
                                </span>
                            </div>

                            {/* Zone context card */}
                            <div className="flex items-center gap-3 bg-indigo-50 border border-indigo-100 rounded-xl px-3.5 py-3">
                                <div className="w-9 h-9 rounded-xl bg-[#2563EB] flex items-center justify-center shrink-0">
                                    <PersonStanding className="w-5 h-5 text-white" />
                                </div>
                                <div>
                                    <p className="text-[13px] font-bold text-gray-800">{activeZone.label}</p>
                                    <p className="text-[11px] text-indigo-500">
                                        {pendingPin?.side === 'front' ? 'Front view' : 'Back view'} · position pinned
                                    </p>
                                </div>
                            </div>

                            {/* Mark type */}
                            <div>
                                <label className="text-[12px] font-semibold text-gray-700 dark:text-gray-200 mb-1.5 block">Mark type <span className="text-red-500">*</span></label>
                                <Select value={markForm.type} onChange={(v) => setMarkForm(f => ({ ...f, type: v }))}
                                    placeholder="Select type..."
                                    options={MARK_TYPES.map((t) => ({ value: t, label: t }))}
                                    className="w-full" />
                            </div>

                            {/* Severity */}
                            <div>
                                <label className="text-[12px] font-semibold text-gray-700 dark:text-gray-200 mb-1.5 block">Severity <span className="text-red-500">*</span></label>
                                <div className="flex gap-2">
                                    {['Mild', 'Moderate', 'Severe'].map(s => (
                                        <button key={s} type="button" onClick={() => setMarkForm(f => ({ ...f, severity: s }))}
                                            className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-[12px] font-semibold border transition ${markForm.severity === s ? 'border-transparent shadow-sm' : 'bg-white dark:bg-[#31373f] text-gray-600 dark:text-gray-400 border-gray-200 dark:border-gray-700 hover:border-gray-300'}`}
                                            style={markForm.severity === s ? { background: SEVERITY_COLOR[s] + '18', borderColor: SEVERITY_COLOR[s], color: SEVERITY_COLOR[s] } : {}}>
                                            <span className="w-2 h-2 rounded-full shrink-0"
                                                style={{ background: markForm.severity === s ? SEVERITY_COLOR[s] : '#D1D5DB' }} />
                                            {s}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Clinical photo */}
                            <div>
                                <label className="text-[12px] font-semibold text-gray-700 dark:text-gray-200 mb-1.5 block">Clinical photo</label>
                                <label className="flex items-center gap-3 border border-dashed border-gray-300 dark:border-gray-700 rounded-lg px-4 py-3 cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition">
                                    <div className="w-8 h-8 rounded-lg bg-gray-100 dark:bg-[#31373f] flex items-center justify-center shrink-0">
                                        <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                                        </svg>
                                    </div>
                                    <div>
                                        <p className="text-[12px] font-semibold text-gray-700 dark:text-gray-200">Add photo</p>
                                        <p className="text-[11px] text-gray-400 dark:text-gray-500">Attach a clinical photograph</p>
                                    </div>
                                    <input type="file" accept="image/*" className="hidden" />
                                </label>
                            </div>

                            {/* Notes */}
                            <div>
                                <label className="text-[12px] font-semibold text-gray-700 dark:text-gray-200 mb-1.5 block">Notes</label>
                                <textarea rows={3} placeholder="Describe the mark, suspected cause, action taken..."
                                    value={markForm.notes} onChange={e => setMarkForm(f => ({ ...f, notes: e.target.value }))}
                                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-[12px] resize-none focus:outline-none focus:ring-2 focus:ring-blue-400 dark:bg-[#31373f] dark:text-gray-200 dark:border-gray-700" />
                            </div>

                            {/* Actions */}
                            <div className="flex gap-2 pt-1">
                                {editingIdx !== null && (
                                    <Button type="button" variant="danger-soft" size="sm" onClick={() => deleteMark(editingIdx)}>
                                        Delete
                                    </Button>
                                )}
                                <Button type="button" variant="secondary" size="sm" className="flex-1" onClick={cancelMark}>
                                    Cancel
                                </Button>
                                <Button type="button" variant="primary" size="sm" className="flex-1" disabled={!markForm.type} onClick={saveMark}>
                                    Save mark
                                </Button>
                            </div>
                        </div>
                    ) : (
                        /* Recorded marks list */
                        <div>
                            <div className="flex items-center justify-between mb-3">
                                <span className="text-[14px] font-bold text-gray-700 dark:text-gray-200">Recorded marks</span>
                                <span className="bg-gray-100 dark:bg-[#31373f] text-gray-600 dark:text-gray-400 text-[11px] font-bold px-2.5 py-1 rounded-full">{markers.length}</span>
                            </div>

                            <div className="flex flex-col gap-2 overflow-y-auto" style={{ maxHeight: 460 }}>
                                {markers.length === 0 ? (
                                    <div className="text-center py-8 text-gray-400 dark:text-gray-500">
                                        <PersonStanding className="w-8 h-8 mx-auto mb-2 opacity-30" />
                                        <p className="text-[12px]">No marks yet.</p>
                                        <p className="text-[11px]">Tap a region on the figure.</p>
                                    </div>
                                ) : markers.map((m, i) => (
                                    <button key={m.id} type="button" onClick={() => openEdit(i)}
                                        className="bg-white dark:bg-[#31373f] border border-gray-100 dark:border-gray-800 rounded-xl p-3 text-left hover:border-blue-200 hover:shadow-sm transition">
                                        <div className="flex items-center gap-2 mb-1">
                                            <span className="w-5 h-5 rounded-full flex items-center justify-center text-white text-[10px] font-bold shrink-0"
                                                style={{ background: SEVERITY_COLOR[m.severity] }}>{i + 1}</span>
                                            <span className="text-[12px] font-semibold text-gray-800 dark:text-gray-200 truncate">{m.type}</span>
                                        </div>
                                        <p className="text-[11px] text-gray-500 dark:text-gray-400 pl-7 truncate">{m.zoneLabel}</p>
                                        <div className="flex items-center gap-1 pl-7 mt-0.5">
                                            <span className="w-1.5 h-1.5 rounded-full" style={{ background: SEVERITY_COLOR[m.severity] }} />
                                            <span className="text-[10px] font-semibold" style={{ color: SEVERITY_COLOR[m.severity] }}>{m.severity}</span>
                                            <span className="text-[10px] text-gray-400 dark:text-gray-500 ml-1">· {m.side}</span>
                                        </div>
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

// ─── Step 4: Wellbeing ────────────────────────────────────────────────────────

function StepWellbeing({ form, setForm }) {
    return (
        <div>
            <h3 className="text-[17px] font-bold text-gray-800 dark:text-gray-200 mb-1">Resident wellbeing</h3>
            <p className="text-[13px] text-gray-500 dark:text-gray-400 mb-5">How is the resident presenting now? Capture emotional state and pain level after the incident.</p>

            <label className="text-[13px] font-semibold text-gray-700 dark:text-gray-200 mb-3 block">Emotional state</label>
            <div className="grid grid-cols-5 gap-2 mb-3">
                {EMOTIONS.map(({ id, label, sub, emoji, color }) => (
                    <button key={id} type="button" onClick={() => setForm(f => ({ ...f, emotion: id }))}
                        className={`flex flex-col items-center gap-1.5 p-3 rounded-xl border transition ${form.emotion === id ? 'bg-gray-50 dark:bg-[#31373f]' : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 bg-white dark:bg-[#31373f]'}`}
                        style={form.emotion === id ? { borderColor: color, borderWidth: 2 } : {}}>
                        <span className="text-2xl">{emoji}</span>
                        <span className="text-[12px] font-semibold text-gray-700 dark:text-gray-200">{label}</span>
                        <span className="text-[10px] text-gray-400 dark:text-gray-500 text-center leading-tight">{sub}</span>
                    </button>
                ))}
            </div>

            <div className="w-full h-1.5 rounded-full mb-5"
                style={{ background: 'linear-gradient(to right, #EF4444, #F97316, #EAB308, #22C55E, #16A34A)' }} />

            <label className="text-[13px] font-semibold text-gray-700 dark:text-gray-200 mb-2 block">
                Pain score <span className="text-[12px] font-normal text-gray-400 dark:text-gray-500">0 = none · 10 = worst imaginable</span>
            </label>
            <div className="flex items-center gap-4 mb-2">
                <input type="range" min={0} max={10} step={1} value={form.pain_score}
                    onChange={e => setForm(f => ({ ...f, pain_score: Number(e.target.value) }))}
                    className="flex-1 accent-[#2563EB]" />
                <div className="w-10 h-10 bg-[#2563EB] rounded-lg flex items-center justify-center text-white font-bold text-[15px] shrink-0">
                    {form.pain_score}
                </div>
            </div>
            <div className="flex justify-between text-[11px] text-gray-400 dark:text-gray-500 mb-5 px-0.5">
                {[0,1,2,3,4,5,6,7,8,9,10].map(n => <span key={n}>{n}</span>)}
            </div>

            <label className="text-[13px] font-semibold text-gray-700 dark:text-gray-200 mb-1.5 block">Wellbeing notes</label>
            <textarea rows={4} placeholder="How the resident responded, any reassurance given, family / next-of-kin contact..."
                value={form.wellbeing_notes} onChange={e => setForm(f => ({ ...f, wellbeing_notes: e.target.value }))}
                className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-[13px] resize-none focus:outline-none focus:ring-2 focus:ring-blue-400 dark:bg-[#31373f] dark:text-gray-200 dark:border-gray-700" />
        </div>
    );
}

// ─── Step 5: Review ───────────────────────────────────────────────────────────

function StepReview({ form, setForm }) {
    const severity  = computeSeverity(form);
    const markers   = form.body_map_markers || [];
    const redFlags  = form.medication_administered || form.signs_of_pain || form.hit_head || form.signs_of_bleeding;
    const emotion   = EMOTIONS.find(e => e.id === form.emotion);

    const severityBg   = { Mild: 'bg-green-50 dark:bg-emerald-900/10 border-green-200 dark:border-emerald-900/50',   Moderate: 'bg-orange-50 dark:bg-amber-900/10 border-orange-200 dark:border-amber-900/50',   Severe: 'bg-red-50 dark:bg-red-900/10 border-red-200 dark:border-red-900/50' };
    const severityText = { Mild: 'text-green-700 dark:text-emerald-400',                  Moderate: 'text-orange-700 dark:text-amber-400',                  Severe: 'text-red-700 dark:text-red-400' };
    const severityIcon = { Mild: '#22C55E',                         Moderate: '#F97316',                          Severe: '#EF4444' };

    const toggleNotify = key => setForm(f => ({ ...f, [key]: !f[key] }));

    const incidentDateTime = form.incident_date
        ? new Date(form.incident_date).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
        : '—';

    return (
        <div>
            <h3 className="text-[17px] font-bold text-gray-800 dark:text-gray-200 mb-1">Review &amp; confirm</h3>
            <p className="text-[13px] text-gray-500 dark:text-gray-400 mb-4">Check the summary, set who needs to be informed, then submit.</p>

            <div className={`flex items-center gap-3 border rounded-xl p-4 mb-5 ${severityBg[severity]}`}>
                <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: severityIcon[severity] + '22' }}>
                    <Shield className="w-5 h-5" style={{ color: severityIcon[severity] }} />
                </div>
                <div>
                    <p className={`text-[14px] font-bold ${severityText[severity]}`}>{severity} severity</p>
                    <p className="text-[12px] text-gray-500 dark:text-gray-400">
                        {redFlags ? 'Clinical red flag(s) recorded.' : 'No clinical red flags recorded.'}
                        {markers.length > 0 ? ` · ${markers.length} body-map mark${markers.length > 1 ? 's' : ''}` : ''}
                    </p>
                </div>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="border border-gray-100 dark:border-gray-800 rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-3">
                        <FileText className="w-4 h-4 text-[#2563EB]" />
                        <span className="text-[12px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Incident</span>
                    </div>
                    {[['Type', form.incident_type || '—'], ['When', incidentDateTime], ['Location', form.location || '—'], ['Witnessed', form.witnessed ? 'Yes' : 'No'], ['Reported by', form.reported_by || '—']].map(([k, v]) => (
                        <div key={k} className="flex justify-between py-1.5 border-b border-gray-50 dark:border-gray-800 last:border-0">
                            <span className="text-[12px] text-gray-500 dark:text-gray-400">{k}</span>
                            <span className="text-[12px] font-medium text-gray-800 dark:text-gray-200">{v}</span>
                        </div>
                    ))}
                </div>

                <div className="border border-gray-100 dark:border-gray-800 rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-3">
                        <Pill className="w-4 h-4 text-[#2563EB]" />
                        <span className="text-[12px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Clinical</span>
                    </div>
                    {[['Medication', form.medication_administered ? 'Yes' : 'No'], ['Pain', form.signs_of_pain ? 'Yes' : 'No'], ['Hit head', form.hit_head ? 'Yes' : 'No'], ['Bleeding', form.signs_of_bleeding ? 'Yes' : 'No'], ['Medical attention', form.medical_attention || 'None']].map(([k, v]) => (
                        <div key={k} className="flex justify-between py-1.5 border-b border-gray-50 dark:border-gray-800 last:border-0">
                            <span className="text-[12px] text-gray-500 dark:text-gray-400">{k}</span>
                            <span className="text-[12px] font-medium text-gray-800 dark:text-gray-200">{v}</span>
                        </div>
                    ))}
                </div>

                <div className="border border-gray-100 dark:border-gray-800 rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-3">
                        <PersonStanding className="w-4 h-4 text-[#2563EB]" />
                        <span className="text-[12px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Body map &amp; wellbeing</span>
                    </div>
                    {[['Marks recorded', markers.length], ['Emotional state', emotion ? `${emotion.emoji} ${emotion.label}` : 'Not recorded'], ['Pain score', `${form.pain_score} / 10`], ['Others involved', form.victims_involved || 'None']].map(([k, v]) => (
                        <div key={k} className="flex justify-between py-1.5 border-b border-gray-50 dark:border-gray-800 last:border-0">
                            <span className="text-[12px] text-gray-500 dark:text-gray-400">{k}</span>
                            <span className="text-[12px] font-medium text-gray-800 dark:text-gray-200">{v}</span>
                        </div>
                    ))}
                </div>

                <div className="border border-gray-100 dark:border-gray-800 rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-3">
                        <AlertTriangle className="w-4 h-4 text-[#2563EB]" />
                        <span className="text-[12px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Notify &amp; follow-up</span>
                    </div>
                    {[['notify_manager', 'Manager / senior on duty'], ['notify_family', 'Family / next of kin'], ['notify_gp', 'GP / district nurse'], ['notify_cqc', 'CQC notifiable event']].map(([key, label]) => (
                        <label key={key} className="flex items-center gap-2.5 py-1.5 cursor-pointer border-b border-gray-50 dark:border-gray-800 last:border-0">
                            <input type="checkbox" checked={!!form[key]} onChange={() => toggleNotify(key)} className="w-4 h-4 accent-[#2563EB]" />
                            <span className="text-[12px] text-gray-700 dark:text-gray-200">{label}</span>
                        </label>
                    ))}
                    <div className="mt-3">
                        <label className="text-[12px] text-gray-500 dark:text-gray-400 block mb-1">Review / follow-up date</label>
                        <input type="date" value={form.follow_up_date}
                            onChange={e => setForm(f => ({ ...f, follow_up_date: e.target.value }))}
                            className="w-full border border-gray-200 rounded-lg px-2.5 py-1.5 text-[12px] focus:outline-none focus:ring-2 focus:ring-blue-400 dark:bg-[#31373f] dark:text-gray-200 dark:border-gray-700" />
                    </div>
                </div>
            </div>
        </div>
    );
}

// ─── Default form state ───────────────────────────────────────────────────────

const defaultForm = () => {
    const now = new Date();
    const pad = n => String(n).padStart(2, '0');
    const local = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}`;
    return {
        incident_type: '', incident_date: local, location: '', witnessed: true, reported_by: '',
        description: '', medication_administered: false, signs_of_pain: false, hit_head: false,
        signs_of_bleeding: false, medical_attention: '', victims_involved: '', body_map_markers: [],
        emotion: 'Calm', pain_score: 0, wellbeing_notes: '',
        notify_manager: true, notify_family: false, notify_gp: false, notify_cqc: false, follow_up_date: '',
    };
};

// ─── Main ─────────────────────────────────────────────────────────────────────

const AddIncidentModal = ({ isOpen, onClose, onSubmit }) => {
    const [step,       setStep]       = useState(0);
    const [form,       setForm]       = useState(defaultForm());
    const [submitting, setSubmitting] = useState(false);

    const reset = () => { setStep(0); setForm(defaultForm()); };
    const handleClose = () => { reset(); onClose(); };

    const canContinue = () => {
        if (step === 0) return !!form.incident_type && !!form.incident_date && !!form.description;
        return true;
    };

    const handleSubmit = async () => {
        setSubmitting(true);
        try {
            await onSubmit({ ...form, severity: computeSeverity(form) });
            reset();
            onClose();
        } finally {
            setSubmitting(false);
        }
    };

    const stepContent = [
        <StepDetails   key="d" form={form} setForm={setForm} />,
        <StepInjury    key="i" form={form} setForm={setForm} />,
        <StepBodyMap   key="b" form={form} setForm={setForm} />,
        <StepWellbeing key="w" form={form} setForm={setForm} />,
        <StepReview    key="r" form={form} setForm={setForm} />,
    ];

    const footer = (
        <div className="flex items-center justify-between pt-2 border-t border-gray-100 dark:border-gray-800">
            <div className="text-[12px] text-gray-400 dark:text-gray-500 font-medium">Step {step + 1} of {STEPS.length}</div>
            <div className="flex gap-2">
                {step > 0 && (
                    <Button type="button" variant="secondary" leftIcon={<ChevronLeft className="w-4 h-4" />} onClick={() => setStep(s => s - 1)}>
                        Back
                    </Button>
                )}
                {step < STEPS.length - 1 ? (
                    <Button type="button" variant="primary" rightIcon={<ChevronRight className="w-4 h-4" />} disabled={!canContinue()} onClick={() => setStep(s => s + 1)}>
                        Continue
                    </Button>
                ) : (
                    <Button type="button" variant="primary" loading={submitting} onClick={handleSubmit}>
                        Submit incident report
                    </Button>
                )}
            </div>
        </div>
    );

    return (
        <Modal isOpen={isOpen} onClose={handleClose} title="New Accident & Incident Report" maxWidth="max-w-4xl" footer={footer}>
            <StepIndicator current={step} />
            {stepContent[step]}
        </Modal>
    );
};

export default AddIncidentModal;
